import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import BrandLogo from '../components/brand/BrandLogo';
import VisualPanel from '../components/common/VisualPanel';
import { FiChevronDown, FiActivity, FiTarget, FiShield, FiCpu, FiTrendingUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-[#13131a] transition-colors hover:border-white/20">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex justify-between items-center text-left focus:outline-none"
      >
        <span className="font-medium text-slate-200">{question}</span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} className="text-slate-500">
          <SafeIcon icon={FiChevronDown} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-4 pt-1 text-sm text-slate-400 border-t border-white/5">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DemoCenter = () => {
  const navigate = useNavigate();

  const steps = [
    { icon: FiCpu, title: "1. Integration", desc: "Connect your CRM and telemetry stack." },
    { icon: FiActivity, title: "2. Scoring", desc: "Define health weights and risk thresholds." },
    { icon: FiShield, title: "3. Detection", desc: "Auto-detect churn signals in real-time." },
    { icon: FiTarget, title: "4. Execution", desc: "Deploy playbooks to at-risk accounts." },
    { icon: FiTrendingUp, title: "5. Retention", desc: "Measure NRR impact and success tasks." }
  ];

  const faqs = [
    { q: "What does ChurnSignal actually do?", a: "ChurnSignal is a predictive telemetry platform for B2B Customer Success teams. It aggregates fragmented customer data to calculate real-time health scores, detect early churn signals, and prescribe retention playbooks. The key outcome is protecting ARR and improving Net Retention Rate (NRR)." },
    { q: "Is this demo using real customer data?", a: "No. All data shown in this environment (Stark Industries, CyberDyne, etc.) is fully simulated dummy data designed to showcase the platform's capabilities. No private data is processed." },
    { q: "How does the interactive demo work?", a: "You can navigate through the sidebar to explore different modules. Try visiting the 'Playbooks' tab to simulate resolving a warning state, or view the 'Accounts' page to see detailed dummy records." },
    { q: "Can I integrate with Salesforce and Zendesk?", a: "Yes. In a live environment, ChurnSignal offers native, real-time integrations with major CRMs (Salesforce, HubSpot), Support Desks (Zendesk), and Product Analytics tools." },
    { q: "How does the health score calculation work?", a: "Health scores are calculated using a weighted algorithm based on Product Usage, Support Ticket volume, Survey Scores (NPS), and Executive Engagement. You can simulate adjusting these weights in the Settings tab." }
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-12 pb-16">
      {/* 1. Overview Section */}
      <section className="text-center space-y-6 pt-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center">
          <BrandLogo size="lg" className="mb-4 scale-125" />
        </motion.div>
        <div className="space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
            The Future of <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-emerald-400">Retention Management</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            ChurnSignal bridges the gap between raw telemetry and executive action. Protect your ARR with automated risk detection and success playbooks.
          </p>
        </div>
      </section>

      {/* Visual Mockup Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest">Real-time Telemetry</h3>
          <VisualPanel type="health" className="h-48" />
        </div>
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest">Revenue Projections</h3>
          <VisualPanel type="retention" className="h-48" />
        </div>
      </div>

      {/* 2. Demo / How It Works */}
      <section className="bg-[#13131a] border border-white/5 rounded-3xl p-8 md:p-12">
        <div className="flex items-center gap-3 mb-10">
          <div className="p-2 bg-emerald-500/10 rounded-lg">
            <SafeIcon icon={FiActivity} className="text-2xl text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">The ChurnSignal Workflow</h2>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6 relative">
          <div className="hidden lg:block absolute top-[28px] left-[10%] right-[10%] h-px bg-white/5 z-0"></div>
          
          {steps.map((step, idx) => (
            <div key={idx} className="relative z-10 flex flex-col items-center text-center group">
              <div className="w-14 h-14 rounded-2xl bg-[#0a0a0f] border border-white/10 flex items-center justify-center mb-4 group-hover:border-violet-500/50 group-hover:shadow-[0_0_20px_rgba(139,92,246,0.2)] transition-all">
                <SafeIcon icon={step.icon} className="text-xl text-slate-400 group-hover:text-violet-400" />
              </div>
              <h3 className="text-xs font-bold text-slate-200 mb-2 uppercase tracking-wide">{step.title}</h3>
              <p className="text-[11px] text-slate-500 leading-relaxed font-medium">{step.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 bg-gradient-to-br from-violet-600/10 to-indigo-600/5 border border-violet-500/20 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-violet-600 flex items-center justify-center animate-pulse">
              <SafeIcon icon={FiTarget} className="text-xl text-white" />
            </div>
            <div>
              <h4 className="text-white font-bold">Interactive Sandbox Active</h4>
              <p className="text-sm text-slate-400">Simulate a customer "save" flow in Playbooks.</p>
            </div>
          </div>
          <button onClick={() => navigate('/playbooks')} className="w-full md:w-auto px-8 py-3 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-violet-900/20 active:scale-95">
            Launch Sandbox Flow
          </button>
        </div>
      </section>

      {/* 5. FAQ Section */}
      <section className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <h2 className="text-2xl font-bold text-white text-center">Frequently Asked Questions</h2>
        </div>
        
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <FaqItem key={idx} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </section>

      <footer className="pt-12 border-t border-white/5 text-center opacity-40">
        <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
          <BrandLogo size="sm" className="opacity-50" />
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </div>
  );
};

export default DemoCenter;